import React, { useCallback, useEffect, useState } from "react";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { fetchConnectors, saveConnector } from "./accountLedgerService";

const g = (o, k) => GoldenSourceHelper.get(o, k);

/**
 * Accounting connectors — which packages are switched on for this site, and (for a
 * global administrator) the app registration each one needs. Connecting a company
 * and pulling its books automatically follows once a package is switched on.
 */
export default function LedgerConnectorsSection() {
  const [rows, setRows] = useState([]);
  const [forms, setForms] = useState({});
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      const r = (await fetchConnectors()).map((x) => ({
        code: g(x, "ProviderCode"), name: g(x, "DisplayName"), enabled: !!g(x, "IsEnabled"),
        configured: !!g(x, "IsConfigured"), clientId: g(x, "ClientId") || "", canAdminister: !!g(x, "CanAdminister"),
      }));
      setRows(r);
      setForms(Object.fromEntries(r.map((x) => [x.code, { clientId: x.clientId, clientSecret: "", isEnabled: x.enabled }])));
    } catch (e) {
      setError(e.message || "Could not load the accounting connectors.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (code) => {
    setBusy(code); setError(""); setNotice("");
    try {
      await saveConnector({ providerCode: code, ...forms[code] });
      await load();
      setNotice("Connector settings saved.");
    } catch (e) {
      setError(e.message || "Could not save.");
    } finally {
      setBusy("");
    }
  };

  const on = rows.filter((r) => r.enabled && r.configured);
  const admin = rows.some((r) => r.canAdminister);
  const setF = (code, k, v) => setForms((f) => ({ ...f, [code]: { ...f[code], [k]: v } }));

  return (
    <>
      {error && <div className="alert alert-danger small">{error}</div>}
      {notice && <div className="alert alert-success small">{notice}</div>}

      {on.length === 0 ? (
        <div className="alert alert-secondary small">
          No accounting connector is switched on for this site yet. You can still import your chart of accounts and
          ledger and map your account codes, or enter the figures by hand below.
        </div>
      ) : (
        <p className="small">
          Switched on: <strong>{on.map((r) => r.name).join(", ")}</strong>. Connecting your company's books to pull the
          chart of accounts and postings automatically is the next step being added.
        </p>
      )}

      {admin && (
        <div className="border rounded p-3 bg-light">
          <div className="fw-bold small mb-1">Accounting connectors — site configuration</div>
          <p className="extra-small text-muted mb-2">
            Global administrators only. Enter this site's app registration from each package's developer portal
            (e.g. developer.xero.com). The secret is stored encrypted and never shown again.
          </p>
          {rows.map((r) => (
            <div key={r.code} className="row g-2 align-items-end mb-2">
              <div className="col-md-2 small fw-bold pb-1">{r.name}</div>
              <div className="col-md-4">
                <input className="form-control form-control-sm" placeholder="Client ID" value={forms[r.code]?.clientId || ""}
                  onChange={(e) => setF(r.code, "clientId", e.target.value)} />
              </div>
              <div className="col-md-3">
                <input type="password" autoComplete="off" className="form-control form-control-sm"
                  placeholder={r.configured ? "Secret stored — blank keeps it" : "Client secret"}
                  value={forms[r.code]?.clientSecret || ""} onChange={(e) => setF(r.code, "clientSecret", e.target.value)} />
              </div>
              <div className="col-md-2">
                <div className="form-check form-switch mb-1">
                  <input id={`acc-${r.code}`} className="form-check-input" type="checkbox" checked={!!forms[r.code]?.isEnabled}
                    onChange={(e) => setF(r.code, "isEnabled", e.target.checked)} />
                  <label className="form-check-label small" htmlFor={`acc-${r.code}`}>Switched on</label>
                </div>
              </div>
              <div className="col-md-1">
                <button type="button" className="btn btn-sm btn-dark w-100" disabled={!!busy} onClick={() => save(r.code)}>
                  {busy === r.code ? "…" : "Save"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {!admin && on.length === 0 && (
        <p className="extra-small text-muted mb-0">A global administrator switches connectors on for the site.</p>
      )}
    </>
  );
}
